import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';

// A code box backed by the public code sets (backend/code_sets.py): type a code
// or words, pick from the list, or keep typing your own. The check under the box
// is advisory — an unknown code WARNS, it never blocks a save or a signature.
//
// kind="icd10"     ICD-10-CM. An old ICD-9 code typed here offers its ICD-10
//                  equivalent; the ICD-9 code itself is never offered.
// kind="procedure" the practice's CPT number (shape-checked only — CPT
//                  descriptors are AMA-licensed, docs/cpt-licensing.md) or a
//                  HCPCS Level II code, which is searched.
export function CodePicker({ kind = 'icd10', value, onChange, onPick, disabled, placeholder, testid, className = '', align = 'left' }) {
  const [results, setResults] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const typed = useRef(false);
  const searchSystem = kind === 'icd10' ? 'icd10cm' : 'hcpcs';
  const checkSystem = kind === 'icd10' ? 'icd10cm' : 'procedure';

  useEffect(() => {
    const q = (value || '').trim();
    if (!typed.current || q.length < 2) { setResults([]); setLegacy([]); return undefined; }
    // A bare CPT number has nothing to search (no licensed descriptors).
    if (kind === 'procedure' && /^\d/.test(q)) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      api.get('/codes/search', { params: { q, system: searchSystem, limit: 12 } })
        .then((r) => { setResults(r.data.results || []); setLegacy(r.data.legacy || []); })
        .catch(() => { setResults([]); setLegacy([]); });
    }, 200);
    return () => clearTimeout(t);
  }, [value, kind, searchSystem]);

  useEffect(() => {
    const code = (value || '').trim();
    // Words are a search, not a code: only check something code-shaped.
    if (!code || /\s/.test(code) || !/\d/.test(code)) { setStatus(null); return undefined; }
    const t = setTimeout(() => {
      api.get('/codes/check', { params: { code, system: checkSystem } })
        .then((r) => setStatus(r.data))
        .catch(() => setStatus(null));
    }, 500);
    return () => clearTimeout(t);
  }, [value, checkSystem]);

  const pick = (item) => {
    typed.current = false;
    setOpen(false);
    onChange(item.code);
    onPick?.(item);
  };

  const showList = open && (results.length > 0 || legacy.length > 0);
  return (
    <div className={`relative ${className}`}>
      <Input
        value={value || ''}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testid}
        autoComplete="off"
        onChange={(e) => { typed.current = true; setOpen(true); onChange(e.target.value); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
      />
      {showList && (
        <div className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} z-50 mt-1 w-[26rem] max-w-[85vw] max-h-72 overflow-auto rounded-md border bg-popover shadow-md text-sm`} role="listbox">
          {legacy.map((old) => (
            <div key={old.code} className="px-3 py-2 border-b bg-muted/40">
              <div className="text-xs text-muted-foreground">Old ICD-9 code {old.code} ({old.short}) — use its ICD-10 equivalent:</div>
              {(old.maps_to || []).map((t) => (
                <button type="button" key={t.code} className="block w-full text-left mt-1 hover:underline" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(t)}>
                  <span className="font-mono">{t.code}</span> {t.description}
                </button>
              ))}
            </div>
          ))}
          {results.map((r) => (
            <button
              type="button"
              key={r.code}
              role="option"
              aria-selected="false"
              className="flex w-full gap-2 px-3 py-1.5 text-left hover:bg-accent"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(r)}
              data-testid={testid ? `${testid}-option` : undefined}
            >
              <span className="font-mono shrink-0 w-16 text-xs pt-0.5">{r.code}</span>
              <span className={r.billable ? '' : 'text-muted-foreground'}>{r.description}{r.billable ? '' : ' (category)'}</span>
            </button>
          ))}
        </div>
      )}
      {status?.warning ? (
        <p className="text-[11px] mt-1 text-[hsl(32_92%_28%)]" data-testid={testid ? `${testid}-warning` : undefined}>{status.warning}</p>
      ) : status?.description ? (
        <p className="text-[11px] mt-1 text-muted-foreground truncate" title={status.description}>{status.description}</p>
      ) : status?.kind === 'cpt' ? (
        <p className="text-[11px] mt-1 text-muted-foreground">CPT code — its description comes from your catalog entry.</p>
      ) : null}
    </div>
  );
}
